import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Link,
  Button,
  useTheme,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LAST_UPDATED = 'February 17, 2025';

function QuranTermsPage() {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.mode === 'dark' ? '#0a0a0a' : '#f5f5f5', py: 4 }}>
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2, color: 'primary.main' }}
        >
          Back
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: '#2e7d32' }}>
            Terms of Service
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
            Quran App / Deen Companion — Last updated: {LAST_UPDATED}
          </Typography>

          <Typography variant="body1" paragraph>
            Welcome to the Quran App (Deen Companion) by Baiglabs. By downloading, installing, or using our app,
            you agree to these Terms of Service. If you do not agree, please do not use the app.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing or using the Quran App, you agree to be bound by these Terms and our Privacy Policy
            (available at{' '}
            <Link href="/quran/privacy" color="primary">
              /quran/privacy
            </Link>
            ). These terms apply to all users of the app.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            2. Description of Service
          </Typography>
          <Typography variant="body1" paragraph>
            The Quran App provides Islamic content including prayer times, Quran text and audio, dhikr (remembrance),
            and faith-based guidance. Content is provided for personal devotional use. We strive for accuracy but
            do not guarantee that prayer times or religious content are error-free or suitable for all methodologies.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            3. Acceptable Use
          </Typography>
          <Typography variant="body1" paragraph>
            You agree to use the app only for lawful purposes and in accordance with these Terms. You must not:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>Use the app in any way that violates applicable laws or regulations</li>
            <li>Attempt to reverse engineer, decompile, or disassemble the app</li>
            <li>Use automated means to access the app without our permission</li>
            <li>Redistribute or commercially exploit our content without authorization</li>
            <li>Interfere with or disrupt the app or its servers</li>
          </Box>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            4. Intellectual Property
          </Typography>
          <Typography variant="body1" paragraph>
            The app, its design, and our original content are owned by Baiglabs. Quranic text and certain Islamic
            content may be sourced from publicly available or licensed materials. You may use the app for personal,
            non-commercial use only.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            5. Disclaimer of Warranties
          </Typography>
          <Typography variant="body1" paragraph>
            The app is provided &quot;as is&quot; and &quot;as available&quot; without warranties of any kind. We do not warrant
            that the app will be uninterrupted, error-free, or free of harmful components. Prayer times and religious
            content are provided for informational purposes; consult qualified scholars for religious rulings.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            6. Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph>
            To the fullest extent permitted by law, Baiglabs shall not be liable for any indirect, incidental,
            special, consequential, or punitive damages arising from your use of the app.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            7. Changes to Terms
          </Typography>
          <Typography variant="body1" paragraph>
            We may modify these Terms at any time. We will post the updated Terms on this page and update the
            &quot;Last updated&quot; date. Your continued use of the app after changes constitutes acceptance of the new Terms.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            8. Contact
          </Typography>
          <Typography variant="body1" paragraph>
            For questions about these Terms, contact us at:
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Link href="https://baiglabs.com/contact" target="_blank" rel="noopener noreferrer" color="primary">
              https://baiglabs.com/contact
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default QuranTermsPage;
